import { useAuthContext } from '@aibees/shared/auth';
import { MemberStatus, MemberType } from '@aibees/shared/definition';
import { useIsOwner, useWorkspace } from '@aibees/shared/workspace';
import { useMemo, useRef } from 'react';

export function useBrowseWorkspaceMembers() {
  const workspace = useWorkspace();
  const { isOwner } = useIsOwner();
  const { user } = useAuthContext();

  const owner = useMemo(
    () => ({
      user: workspace?.user,
      created: workspace?.created,
      type: MemberType.Owner,
      status: MemberStatus.Active,
    }),
    [workspace]
  );
  const loggedInUser = useMemo(
    () =>
      isOwner ? owner : workspace?.members.find((x) => x.user?.id === user?.id),
    [isOwner, user, workspace, owner]
  );
  const actives = useMemo(
    () =>
      workspace?.members
        ?.filter((x) => x.user?.id !== user?.id)
        ?.filter((x) => x.status === MemberStatus.Active)
        .filter((x) => owner.user?.id !== x.user?.id)
        .sort((a) => (a.type === MemberType.Admin ? -1 : 1)) || [],
    [workspace, user]
  );
  const pendings = useMemo(
    () =>
      workspace?.members
        ?.filter((x) => x.user?.id !== user?.id)
        ?.filter((x) => x.status === MemberStatus.Pending)
        .sort((a) => (a.type === MemberType.Admin ? -1 : 1)) || [],
    [workspace, user]
  );
  const disableds = useMemo(
    () =>
      workspace?.members
        ?.filter((x) => x.user?.id !== user?.id)
        ?.filter((x) => x.status === MemberStatus.disable)
        .sort((a) => (a.type === MemberType.Admin ? -1 : 1)) || [],
    [workspace, user]
  );

  const deleteds = useMemo(
    () =>
      workspace?.members
        ?.filter((x) => x.user?.id !== user?.id)
        ?.filter((x) => x.status === MemberStatus.Deleted)
        .sort((a) => (a.type === MemberType.Admin ? -1 : 1)) || [],
    [workspace, user]
  );

  const browseApiData = useMemo(
    () =>
      isOwner
        ? [...[owner], ...actives, ...pendings, ...disableds, ...deleteds]
        : [
            ...[owner],
            ...[loggedInUser],
            ...actives,
            ...pendings,
            ...disableds,
            ...deleteds,
          ],
    [isOwner, loggedInUser, owner, actives, pendings, disableds, deleteds]
  );

  // Return a stable browseApi reference when the list content is unchanged (by length + first/last id).
  // Content-based key avoids new refs when workspace/owner/actives etc. change reference but list is the same.
  const dataLen = browseApiData.length;
  const firstId = browseApiData[0]?.user?.id ?? '';
  const lastId = dataLen ? browseApiData[dataLen - 1]?.user?.id ?? '' : '';
  const contentSig = `${dataLen}_${firstId}_${lastId}`;

  const cacheRef = useRef<{
    sig: string;
    api: { data: typeof browseApiData };
  }>({ sig: '', api: { data: [] } });
  if (cacheRef.current.sig !== contentSig) {
    cacheRef.current = {
      sig: contentSig,
      api: { data: browseApiData },
    };
  } else {
    cacheRef.current.api.data = browseApiData;
  }
  const browseApi = cacheRef.current.api;

  return useMemo(() => ({ browseApi }), [browseApi]);
}
